/* ============================================================================
   rbk-glsl.js — gated WebGL shader hero for rp-hero sections.
   ROLLBACK: delete this file + its <script> tag in layout/theme.liquid;
   heroes keep their static image (the shader only ever crossfades on top).
   ----------------------------------------------------------------------------
   Implements the brief's "limited WebGL" spec with a hand-rolled ~6KB runner
   instead of lazy-loading three.js (Step 5: lightest tool that delivers).
   The effect: brand-toned gradient flow + simplex-style noise displacement +
   film grain, drifting slowly and reacting subtly to scroll. No raymarching,
   no particles, no shadows.

   Gates (ALL must pass or nothing initializes and the static hero stays):
   - prefers-reduced-motion is not reduce
   - navigator.deviceMemory >= 4 when reported
   - connection is not 2g/3g/slow-4g and saveData is off
   - a webgl context is actually obtainable
   Caps: devicePixelRatio capped at 1.75, ~36fps frame cap, render loop fully
   paused off-screen via IntersectionObserver, renderer disposed on
   shopify:section:unload and pagehide.
   ========================================================================== */
(function () {
  'use strict';

  function gatesPass() {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (navigator.deviceMemory && navigator.deviceMemory < 4) return false;
      var c = navigator.connection;
      if (c) {
        if (c.saveData) return false;
        if (/(^|-)2g|3g/.test(c.effectiveType || '')) return false;
      }
      /* RBK PERF (2026-07-06): never run the WebGL hero on phones/tablets. Mobile GPUs are
         weak and the shader loop costs seconds of main-thread time there — Lighthouse measured
         ~4.7s of scripting on throttled mobile, which is the biggest single hit to TBT/score.
         Desktop-only keeps the effect where a real GPU makes it ~150ms. */
      if (window.innerWidth < 1025) return false;
      /* Bail when WebGL is running in SOFTWARE (SwiftShader / llvmpipe / Basic Render Driver).
         That's exactly what Lighthouse/PSI's headless Chrome uses, and what GPU-blocklisted
         machines fall back to — there, a shader that's ~150ms on a real GPU balloons to 20s+
         of CPU (this was the 21,000ms desktop TBT in PageSpeed Insights). Only run on a genuine
         hardware GPU. */
      var tc = document.createElement('canvas');
      var gl = tc.getContext('webgl') || tc.getContext('experimental-webgl');
      if (!gl) return false;
      var dbg = gl.getExtension('WEBGL_debug_renderer_info');
      var rend = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '') : '';
      var lose = gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext();
      if (/swiftshader|llvmpipe|software|basic render|microsoft basic/i.test(rend)) return false;
    } catch (e) { return false; }
    return true;
  }

  /* 11 effect modes share one program; u_mode switches the branch.
     0 ember (original flow) · 1 neon bands · 2 smoke · 3 pulse · 4 sweep ·
     5 glitch · 6 aurora · 7 noir · 8 haze · 9 signal · 10 plasma */
  var FX = { ember: 0, neon: 1, smoke: 2, pulse: 3, sweep: 4, glitch: 5, aurora: 6, noir: 7, haze: 8, signal: 9, plasma: 10 };
  var FRAG = [
    'precision mediump float;',
    'uniform vec2 u_res; uniform float u_t; uniform float u_scroll; uniform float u_mode;',
    'uniform vec3 u_a; uniform vec3 u_b; uniform vec3 u_c;',
    'float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }',
    'float noise(vec2 p){ vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y); }',
    'float fbm(vec2 p){ float v = 0.0; float a = 0.55; for (int i = 0; i < 4; i++){ v += a * noise(p); p *= 2.05; a *= 0.5; } return v; }',
    'void main(){',
    '  vec2 uv = gl_FragCoord.xy / u_res;',
    '  vec2 q = uv * vec2(u_res.x / u_res.y, 1.0);',
    '  float t = u_t * 0.05;',
    '  vec3 col;',
    '  if (u_mode < 0.5) {',
    '    vec2 warp = vec2(fbm(q * 1.6 + t), fbm(q * 1.6 - t + 4.7));',
    '    float n = fbm(q * 1.2 + warp * 0.9 + vec2(0.0, u_scroll * 0.18));',
    '    col = mix(u_a, u_b, smoothstep(0.18, 0.78, n));',
    '    col = mix(col, u_c, smoothstep(0.62, 0.95, fbm(q * 2.2 - warp + t * 0.6)) * 0.5);',
    '  } else if (u_mode < 1.5) {',
    '    float band = sin(q.x * 5.0 + fbm(q * 2.0 + t * 2.0) * 4.0 + u_t * 0.32);',
    '    col = mix(u_a, u_b, smoothstep(-0.6, 0.9, band));',
    '    col += u_c * pow(max(0.0, sin(q.y * 7.0 - u_t * 0.5 + fbm(q * 3.0) * 3.0)), 6.0) * 0.55;',
    '  } else if (u_mode < 2.5) {',
    '    float n = fbm(q * 0.9 + vec2(fbm(q * 1.3 + t * 0.5), fbm(q * 1.3 - t * 0.4)) * 1.4);',
    '    col = mix(u_a, u_b, smoothstep(0.2, 0.9, n));',
    '    col = mix(col, u_c * 0.4 + col * 0.6, smoothstep(0.75, 1.0, n));',
    '  } else if (u_mode < 3.5) {',
    '    float d = distance(uv, vec2(0.5, 0.46));',
    '    float pulse = 0.5 + 0.5 * sin(u_t * 0.9 - d * 7.0);',
    '    col = mix(u_a, u_b, smoothstep(0.85, 0.1, d));',
    '    col = mix(col, u_c, pulse * smoothstep(0.6, 0.0, d) * 0.45 + fbm(q * 2.0 + t) * 0.1);',
    '  } else if (u_mode < 4.5) {',
    '    float sweep = fract((q.x + q.y) * 0.4 - u_t * 0.06);',
    '    float beam = smoothstep(0.0, 0.45, sweep) * smoothstep(0.9, 0.45, sweep);',
    '    col = mix(u_a, u_b, fbm(q * 1.4 + t));',
    '    col = mix(col, u_c, beam * 0.5);',
    '  } else if (u_mode < 5.5) {',
    '    float row = floor(uv.y * 36.0);',
    '    float tick = step(0.965, hash(vec2(row, floor(u_t * 2.2))));',
    '    vec2 g = uv; g.x += (hash(vec2(row, floor(u_t * 2.2) + 1.0)) - 0.5) * 0.18 * tick;',
    '    float n = fbm(g * vec2(u_res.x / u_res.y, 1.0) * 1.5 + t);',
    '    col = mix(u_a, u_b, smoothstep(0.2, 0.85, n));',
    '    col = mix(col, u_c, tick * 0.5 + smoothstep(0.7, 0.95, n) * 0.3);',
    '  } else if (u_mode < 6.5) {',
    '    float curt = fbm(vec2(q.x * 2.6, q.y * 0.5 - u_t * 0.07));',
    '    col = mix(u_a, u_b, smoothstep(0.25, 0.8, curt));',
    '    col += u_c * pow(curt, 3.0) * (0.35 + 0.2 * sin(u_t * 0.4 + q.x * 3.0));',
    '  } else if (u_mode < 7.5) {',
    '    float n = fbm(q * 1.5 + vec2(t, -t));',
    '    float g = mix(0.06, 0.5, smoothstep(0.15, 0.9, n));',
    '    col = vec3(g);',
    '    col += u_c * smoothstep(0.82, 0.98, n) * 0.18;',
    '  } else if (u_mode < 8.5) {',
    '    vec2 h = q; h.x += sin(h.y * 9.0 + u_t * 0.8) * 0.025; h.y += fbm(q * 2.0 + t) * 0.12 - u_t * 0.012;',
    '    float n = fbm(h * 1.4);',
    '    col = mix(u_a, u_b, smoothstep(0.2, 0.85, n));',
    '    col = mix(col, u_c, smoothstep(0.65, 0.95, fbm(h * 2.4)) * 0.4);',
    '  } else if (u_mode < 9.5) {',
    '    float scan = 0.85 + 0.15 * sin(uv.y * u_res.y * 1.6);',
    '    float flick = 0.94 + 0.06 * hash(vec2(floor(u_t * 8.0), 1.0));',
    '    float n = fbm(q * 1.3 + t * 1.4);',
    '    col = mix(u_a, u_b, smoothstep(0.2, 0.8, n)) * scan * flick;',
    '    col += u_c * smoothstep(0.7, 0.95, n) * 0.35;',
    '  } else {',
    '    float p1 = sin(q.x * 3.1 + u_t * 0.4) + sin(q.y * 2.7 - u_t * 0.32) + sin((q.x + q.y) * 2.2 + u_t * 0.25);',
    '    col = mix(u_a, u_b, 0.5 + 0.33 * sin(p1));',
    '    col = mix(col, u_c, pow(0.5 + 0.5 * sin(p1 * 1.7 + u_t * 0.2), 3.0) * 0.5);',
    '  }',
    '  float vig = smoothstep(1.25, 0.35, distance(uv, vec2(0.5, 0.42)));',
    '  col *= mix(0.72, 1.0, vig);',
    '  float grain = (hash(gl_FragCoord.xy + fract(u_t) * 100.0) - 0.5) * 0.07;',
    '  gl_FragColor = vec4(col + grain, 1.0);',
    '}'
  ].join('\n');

  function hex(h) {
    h = h.replace('#', '');
    if (h.length === 3) h = h.replace(/./g, function (c) { return c + c; });
    var n = parseInt(h, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  function mount(host) {
    if (host.__rpGlsl) return;
    var canvas = document.createElement('canvas');
    canvas.className = 'rp-hero__glsl';
    /* accent mode: blends ON TOP of a photographic hero (soft-light, partial opacity)
       instead of replacing it — used on the homepage where the photo is the content. */
    if (host.getAttribute('data-rp-glsl-mode') === 'accent') canvas.className += ' rp-hero__glsl--accent';
    canvas.setAttribute('aria-hidden', 'true');
    var gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' });
    if (!gl) return;
    host.appendChild(canvas);

    function sh(type, src) {
      var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) return null;
      return s;
    }
    var vs = sh(gl.VERTEX_SHADER, 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }');
    var fs = sh(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) { canvas.remove(); return; }
    var prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog); gl.useProgram(prog);
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uRes = gl.getUniformLocation(prog, 'u_res');
    var uT = gl.getUniformLocation(prog, 'u_t');
    var uScroll = gl.getUniformLocation(prog, 'u_scroll');
    gl.uniform3fv(gl.getUniformLocation(prog, 'u_a'), hex(host.getAttribute('data-rp-glsl-a') || '#0d0e12'));
    gl.uniform3fv(gl.getUniformLocation(prog, 'u_b'), hex(host.getAttribute('data-rp-glsl-b') || '#2a0d0d'));
    gl.uniform3fv(gl.getUniformLocation(prog, 'u_c'), hex(host.getAttribute('data-rp-glsl-c') || '#c01818'));
    var fxName = host.getAttribute('data-rp-glsl-fx') || 'ember';
    gl.uniform1f(gl.getUniformLocation(prog, 'u_mode'), FX[fxName] !== undefined ? FX[fxName] : 0);

    var dpr = Math.min(window.devicePixelRatio || 1, 1.75);
    function resize() {
      var w = host.clientWidth, h = host.clientHeight;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uRes, canvas.width, canvas.height);
    }
    resize();
    window.addEventListener('resize', resize);

    var visible = true, raf = null, last = 0, FRAME = 1000 / 36;
    function loop(now) {
      raf = requestAnimationFrame(loop);
      if (!visible) return;
      if (now - last < FRAME) return;
      last = now;
      gl.uniform1f(uT, now / 1000);
      gl.uniform1f(uScroll, (window.pageYOffset || 0) / Math.max(1, window.innerHeight));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!canvas.classList.contains('is-live')) canvas.classList.add('is-live');
    }
    raf = requestAnimationFrame(loop);

    var io = new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }, { threshold: 0.01 });
    io.observe(host);

    function dispose() {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('resize', resize);
      try { gl.getExtension('WEBGL_lose_context').loseContext(); } catch (e) {}
      canvas.remove();
      host.__rpGlsl = null;
    }
    host.__rpGlsl = { dispose: dispose };
    window.addEventListener('pagehide', dispose, { once: true });
  }

  function boot(root) {
    if (!gatesPass()) return;
    (root || document).querySelectorAll('[data-rp-glsl]').forEach(function (host) {
      /* defer mount until the page is interactive so it never touches the LCP clock */
      if (document.readyState === 'complete') setTimeout(function () { mount(host); }, 600);
      else window.addEventListener('load', function () { setTimeout(function () { mount(host); }, 600); }, { once: true });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var host = e.target.querySelector('[data-rp-glsl]');
    if (host && host.__rpGlsl) host.__rpGlsl.dispose();
  });
})();
